import {
  getAndroidVersion,
  getIosVersion,
  getSdkVersion,
  isAndroid,
  isExternalSdk,
  isInternalSdk,
  isIos,
  isWeb,
  ua,
  versionIsAboveOrEqual,
  versionIsStrictlyAbove
} from './environment-utils';

interface VersionOptions<T> {
  equal?: T;
  lowerOrEqualTo?: T;
  greaterOrEqualTo?: T;
  lowerThan?: T;
  greaterThan?: T;
}

type Version = string;

interface SystemsOptions {
  android?: VersionOptions<number> | boolean;
  ios?: VersionOptions<number> | boolean;
  androidSdk?: VersionOptions<Version>;
  iosSdk?: VersionOptions<Version>;
  externalSdk?: boolean;
  web?: boolean;
}

const checkNumber = (version: number, system: VersionOptions<number>) => {
  return (
    (system.equal && version === system.equal) ||
    (system.lowerOrEqualTo && version <= system.lowerOrEqualTo) ||
    (system.greaterOrEqualTo && version >= system.greaterOrEqualTo) ||
    (system.lowerThan && version < system.lowerThan) ||
    (system.greaterThan && version > system.greaterThan) ||
    false
  );
};

const checkVersion = (version: Version, system: VersionOptions<Version>) => {
  return (
    (system.equal && system.equal === version) ||
    (system.lowerOrEqualTo &&
      versionIsAboveOrEqual(system.lowerOrEqualTo, version)) ||
    (system.greaterOrEqualTo &&
      versionIsAboveOrEqual(version, system.greaterOrEqualTo)) ||
    (system.lowerThan && versionIsStrictlyAbove(system.lowerThan, version)) ||
    (system.greaterThan &&
      versionIsStrictlyAbove(version, system.greaterThan)) ||
    false
  );
};

let loggedUserAgentError = false;
function logUAErrorToSentry(message: string, extra?: any) {
  const Raven = window.Raven;
  if (Raven && !loggedUserAgentError) {
    loggedUserAgentError = true;
    // cannot use logMessage here because of circular dependency
    if (extra) {
      Raven.setTagsContext(extra);
    }
    Raven.captureMessage(message);
  }
}

/// isEnabledOn: this is an optout function
// by default it return false unless you set ios / android to true
// if you set only external, it optin for both android and ios
// if you set multiple VersionOptions, it does a logical ||
// check tests for more infos
export function isEnabledOn(systems?: SystemsOptions) {
  if (
    !systems ||
    typeof systems !== 'object' ||
    Object.keys(systems).length === 0
  ) {
    return false;
  }

  if (isWeb()) {
    return Boolean(systems.web);
  }

  // force specific os to true if we enable on this os sdk
  if (
    typeof systems.android === 'undefined' &&
    typeof systems.androidSdk === 'object'
  ) {
    systems.android = true;
  }
  if (
    typeof systems.ios === 'undefined' &&
    typeof systems.iosSdk === 'object'
  ) {
    systems.ios = true;
  }

  // Bypass web key as it is used above
  const { web: _, ...rest } = systems;
  const keys = Object.keys(rest);
  const isExternalTheOnlyKey = keys.length === 1 && keys[0] === 'externalSdk';
  if (isExternalTheOnlyKey && isExternalSdk()) {
    return systems.externalSdk || false;
  }

  if (isAndroid()) {
    if (isInternalSdk()) {
      if (typeof systems.androidSdk === 'object') {
        const system: VersionOptions<string> = systems.androidSdk;
        if (!checkVersion(getSdkVersion() || '', system)) {
          return false;
        }
      }
    } else {
      if (!systems.externalSdk) {
        return false;
      }
    }

    if (typeof systems.android === 'undefined') {
      return false;
    }
    if (typeof systems.android === 'boolean') {
      if (!systems.android) {
        return false;
      }
    }
    if (typeof systems.android === 'object') {
      const system: VersionOptions<number> = systems.android;
      if (!checkNumber(getAndroidVersion(), system)) {
        return false;
      }
    }
  } else if (isIos()) {
    if (isInternalSdk()) {
      if (typeof systems.iosSdk === 'object') {
        const system: VersionOptions<string> = systems.iosSdk;
        if (!checkVersion(getSdkVersion() || '', system)) {
          return false;
        }
      }
    } else {
      if (!systems.externalSdk) {
        return false;
      }
    }

    if (typeof systems.ios === 'undefined') {
      return false;
    }
    if (typeof systems.ios === 'boolean') {
      if (!systems.ios) {
        return false;
      }
    }
    if (typeof systems.ios === 'object') {
      const system: VersionOptions<number> = systems.ios;
      if (!checkNumber(getIosVersion(), system)) {
        return false;
      }
    }
  } else {
    logUAErrorToSentry('Format error: system not detected', {
      userAgent: ua()
    });
    return false;
  }
  return true;
}

/// isDisabledOn : alias, making this next instructions equals
// isDisabledOn({ externalSdk: true }),
// isEnabledOn({
//   android: true,
//   ios: true,
//   externalSdk: false
// }),
export function isDisabledOn(systems: SystemsOptions) {
  return !isEnabledOn(systems);
}
